import {
  BarChart3,
  CreditCard,
  DollarSign,
  Settings,
  Shield,
  Zap,
} from 'lucide-react'
import { Card, CardContent } from '@/shared/components/ui/card'

export function FeaturesShowcase() {
  // Simple animation component without feature rotation

  return (
    <div className='min-h-screen bg-background p-4 md:p-8'>
      {/* Decorative background elements */}
      <div className='fixed inset-0 overflow-hidden pointer-events-none'>
        <div className='absolute top-10 left-10 w-20 h-20 bg-muted/30 rounded-full blur-sm' />
        <div className='absolute top-32 right-20 w-16 h-32 bg-muted/20 rounded-full blur-sm' />
        <div className='absolute bottom-20 left-16 w-24 h-12 bg-muted/25 rounded-full blur-sm' />
        <div className='absolute top-1/2 left-1/4 w-18 h-18 bg-muted/20 rounded-full blur-sm' />
        <div className='absolute bottom-32 right-32 w-20 h-20 bg-muted/30 rounded-full blur-sm' />
        <div className='absolute top-20 left-1/2 w-14 h-28 bg-muted/15 rounded-full blur-sm' />
        <div className='absolute bottom-10 left-1/3 w-22 h-22 bg-muted/25 rounded-full blur-sm' />
        <div className='absolute top-1/3 right-10 w-16 h-16 bg-muted/20 rounded-full blur-sm' />
      </div>

      {/* Main grid container */}
      <div className='relative max-w-lg mx-auto'>
        <div className='grid grid-cols-2 gap-4 h-[900px] md:h-[800px] animate-grid-float'>
          {/* Row 1 */}
          <Card
            className='bg-muted/50 border-muted/20 backdrop-blur-sm overflow-hidden animate-box-float'
            style={{ animationDelay: '0ms' }}
          >
            <CardContent className='relative p-6 h-full flex flex-col justify-between'>
              <div className='absolute top-4 left-4 opacity-20'>
                <div className='flex flex-wrap gap-1 w-16'>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={i}
                      className='w-1 h-1 bg-current rounded-full animate-pulse'
                      style={{ animationDelay: `${i * 100}ms` }}
                    />
                  ))}
                </div>
              </div>
              <BarChart3 className='w-6 h-6 self-end text-muted-foreground opacity-70' />
              <div>
                <h3 className='text-sm font-semibold leading-tight text-foreground'>
                  ROI
                </h3>
                <h3 className='text-sm font-semibold leading-tight text-foreground'>
                  Monitoring
                </h3>
              </div>
            </CardContent>
          </Card>

          <Card
            className='bg-gradient-to-br from-slate-600 to-slate-700 border-slate-500/20 text-white animate-box-float'
            style={{ animationDelay: '150ms' }}
          >
            <CardContent className='relative p-6 h-full flex flex-col justify-between'>
              <div className='absolute top-4 left-4 opacity-20'>
                <div className='flex flex-wrap gap-1 w-16'>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={i}
                      className='w-1 h-1 bg-current rounded-full animate-pulse'
                      style={{ animationDelay: `${i * 100}ms` }}
                    />
                  ))}
                </div>
              </div>
              <CreditCard className='w-6 h-6 self-end text-white opacity-70' />
              <div>
                <h3 className='text-sm font-semibold leading-tight'>
                  Automated
                </h3>
                <h3 className='text-sm font-semibold leading-tight'>Billing</h3>
              </div>
            </CardContent>
          </Card>

          {/* Row 2 */}
          <Card
            className='bg-gradient-to-br from-slate-600 to-slate-700 border-slate-500/20 text-white animate-box-float'
            style={{ animationDelay: '300ms' }}
          >
            <CardContent className='relative p-6 h-full flex flex-col justify-between'>
              <div className='absolute top-4 left-4 opacity-20'>
                <div className='flex flex-wrap gap-1 w-16'>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={i}
                      className='w-1 h-1 bg-current rounded-full animate-pulse'
                      style={{ animationDelay: `${i * 100}ms` }}
                    />
                  ))}
                </div>
              </div>
              <Shield className='w-6 h-6 self-end opacity-70' />
              <div>
                <h3 className='text-sm font-semibold leading-tight'>
                  Entitlements
                </h3>
                <h3 className='text-sm font-semibold leading-tight'>Check</h3>
              </div>
            </CardContent>
          </Card>

          <Card
            className='bg-gradient-to-br from-slate-200 to-slate-300 border-slate-200/20 text-slate-900 animate-box-float'
            style={{ animationDelay: '450ms' }}
          >
            <CardContent className='relative p-6 h-full flex flex-col justify-between'>
              <div className='absolute top-4 left-4 opacity-20'>
                <div className='flex flex-wrap gap-1 w-16'>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={i}
                      className='w-1 h-1 bg-current rounded-full animate-pulse'
                      style={{ animationDelay: `${i * 100}ms` }}
                    />
                  ))}
                </div>
              </div>
              <Zap className='w-6 h-6 self-end opacity-70' />
              <div>
                <h3 className='text-sm font-semibold leading-tight'>
                  Real-time
                </h3>
                <h3 className='text-sm font-semibold leading-tight'>
                  Metering
                </h3>
              </div>
            </CardContent>
          </Card>

          {/* Row 3 */}
          <Card
            className='bg-gradient-to-br from-slate-200 to-slate-300 border-slate-200/20 text-slate-900 animate-box-float'
            style={{ animationDelay: '600ms' }}
          >
            <CardContent className='relative p-6 h-full flex flex-col justify-between'>
              <div className='absolute top-4 left-4 opacity-20'>
                <div className='flex flex-wrap gap-1 w-16'>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={i}
                      className='w-1 h-1 bg-current rounded-full animate-pulse'
                      style={{ animationDelay: `${i * 100}ms` }}
                    />
                  ))}
                </div>
              </div>
              <DollarSign className='w-6 h-6 self-end opacity-70' />
              <div>
                <h3 className='text-sm font-semibold leading-tight'>Agent</h3>
                <h3 className='text-sm font-semibold leading-tight'>
                  Monetization
                </h3>
              </div>
            </CardContent>
          </Card>

          <Card
            className='bg-muted/50 border-muted/20 backdrop-blur-sm overflow-hidden animate-box-float'
            style={{ animationDelay: '750ms' }}
          >
            <CardContent className='relative p-6 h-full flex flex-col justify-between'>
              <div className='absolute top-4 left-4 opacity-20'>
                <div className='flex flex-wrap gap-1 w-16'>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={i}
                      className='w-1 h-1 bg-current rounded-full animate-pulse'
                      style={{ animationDelay: `${i * 100}ms` }}
                    />
                  ))}
                </div>
              </div>
              <Settings className='w-6 h-6 self-end text-muted-foreground opacity-70' />
              <div>
                <h3 className='text-sm font-semibold leading-tight text-foreground'>
                  Plan
                </h3>
                <h3 className='text-sm font-semibold leading-tight text-foreground'>
                  Management
                </h3>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
